import { useState } from "react";
import {
  Button,
  CircularProgress,
  List,
  Stack,
  Typography,
} from "@mui/material";
import { usePagePrefs } from "@pepsico/pref-permissions";

import { useGetGrowersGeoByLocation } from "api/growers-geo/growers-geo";
import type { GrowerGeoResponse, SiteGeoResponse } from "api/model";
import { useGetSitesGeoByLocation } from "api/sites-geo/sites-geo";
import { usePlotMap } from "context/PlotMap";
import { hasBoundaryGeoJson } from "context/PlotMap/boundaryUtils";

import AssignSitesToGrowersEditor from "./AssignSitesToGrowersEditor";
import SiteBoundaryItem from "./SiteBoundaryItem";

interface SiteBoundariesSectionProps {}

const getGrowerLabel = (grower: GrowerGeoResponse): string =>
  grower.grower_label?.trim() || "Unlabelled Grower Boundary";

const SiteBoundariesSection: React.FC<SiteBoundariesSectionProps> = () => {
  const [prefs] = usePagePrefs();
  const {
    boundary: {
      editingSiteGeoId,
      isDirty,
      isGrowerBoundaryMode,
      isSiteBoundaryMode,
    },
  } = usePlotMap();
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);

  const locationUuid = (prefs.location as string | null | undefined) ?? "";
  const { data: growers = [], isPending: isGrowersPending } =
    useGetGrowersGeoByLocation(
      { location_uuid: locationUuid },
      {
        query: {
          enabled: locationUuid !== "",
          select: (res): GrowerGeoResponse[] =>
            res.status === 200 ? res.data : [],
        },
      },
    );
  const {
    data: sites = [],
    isFetching,
    isPending,
  } = useGetSitesGeoByLocation(undefined, {
    query: {
      select: (res): SiteGeoResponse[] => (res.status === 200 ? res.data : []),
    },
  });

  const isLoadingSites = isPending && sites.length === 0;
  const hasAnyGrowerBoundary = growers.some((grower) =>
    hasBoundaryGeoJson(grower.boundary),
  );
  const growerIds = new Set(growers.map((grower) => grower.id));
  const sitesForGroups = isSiteBoundaryMode
    ? sites.filter((site) => site.id === editingSiteGeoId)
    : sites;
  const unassignedCount = sites.filter((site) => site.grower_id == null).length;
  const sitesByGrowerId = new Map<number, SiteGeoResponse[]>();
  for (const site of sitesForGroups) {
    if (site.grower_id == null || !growerIds.has(site.grower_id)) {
      continue;
    }

    const group = sitesByGrowerId.get(site.grower_id) ?? [];
    group.push(site);
    sitesByGrowerId.set(site.grower_id, group);
  }
  const growerGroups = growers.flatMap((grower) => {
    const groupSites = sitesByGrowerId.get(grower.id);
    return groupSites == null ? [] : [{ grower, sites: groupSites }];
  });
  const assignedCount = growerGroups.reduce(
    (count, group) => count + group.sites.length,
    0,
  );
  const canAssign =
    hasAnyGrowerBoundary &&
    !isGrowerBoundaryMode &&
    !isSiteBoundaryMode &&
    !isDirty;

  const handleStartAssign = () => {
    if (!canAssign) {
      return;
    }
    setAssignDialogOpen(true);
  };

  const handleCancelAssign = () => {
    setAssignDialogOpen(false);
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Typography
          component="h3"
          sx={{ fontWeight: "bold" }}
          variant="subtitle2"
        >
          SITES
        </Typography>
        {!isSiteBoundaryMode && unassignedCount > 0 && (
          <Button
            aria-label="Assign sites to grower boundaries"
            disabled={!canAssign}
            onClick={handleStartAssign}
            size="small"
            variant="contained"
          >
            {`Assign (${unassignedCount})`}
          </Button>
        )}
      </Stack>
      {isLoadingSites && (
        <CircularProgress aria-label="Loading site boundaries" size={24} />
      )}
      {!isLoadingSites && assignedCount === 0 && unassignedCount === 0 && (
        <Typography color="text.secondary" variant="body2">
          No sites found.
        </Typography>
      )}
      {!isGrowersPending && !hasAnyGrowerBoundary && unassignedCount > 0 && (
        <Typography color="text.secondary" variant="body2">
          Draw a grower boundary before assigning sites.
        </Typography>
      )}
      {isFetching && !isLoadingSites && (
        <CircularProgress aria-label="Refreshing site boundaries" size={20} />
      )}
      {growerGroups.map(({ grower, sites: groupSites }) => (
        <Stack key={grower.id} spacing={1}>
          <Typography sx={{ fontWeight: "bold" }} variant="subtitle1">
            {getGrowerLabel(grower)}
          </Typography>
          <List
            aria-label={`Sites for ${getGrowerLabel(grower)}`}
            disablePadding
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              pl: 2.5,
            }}
          >
            {groupSites.map((site) => (
              <SiteBoundaryItem key={site.id} siteGeoId={site.id} />
            ))}
          </List>
        </Stack>
      ))}
      <AssignSitesToGrowersEditor
        onCancel={handleCancelAssign}
        open={assignDialogOpen}
      />
    </Stack>
  );
};

export default SiteBoundariesSection;
